import React, { memo, useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Spinner from "../Spinner";
import MetricTrendChart from "../charts/MetricTrendChart";
import SpirometryResultRow from "./rows/SpirometryResultRow";
import { useSpirometryResults } from "../../hooks/results/useSpirometryResults";
import { useTranslation } from "react-i18next";
import { getValidatedMeasurementValue } from "../../utils/results/validatedRanges";
import {
  DEFAULT_RESULTS_PAGE_SIZE,
  useInfiniteResultsPageSize,
} from "../../hooks/results/useInfiniteResultsPageSize";

function labelDdMm(ms) {
  const d = new Date(ms);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}`;
}

export default memo(function SpirometryResults({ db, uid, subjectId }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [metric, setMetric] = useState("FEV1"); // "FEV1" | "PEF"
  const [pageSize, setPageSize] = useState(DEFAULT_RESULTS_PAGE_SIZE);
  const resetKey = `${uid || ""}:${subjectId || ""}:spiro`;
  const { rows, loading, loadingMore, hasMore, error } = useSpirometryResults(
    db,
    uid,
    {
      pageSize,
      subjectId,
    },
  );
  const { handleScroll } = useInfiniteResultsPageSize({
    canLoadMore: hasMore,
    loading,
    loadingMore,
    rowsLength: rows.length,
    resetKey,
    pageSize,
    setPageSize,
    initialPageSize: DEFAULT_RESULTS_PAGE_SIZE,
  });

  const onRowClick = useCallback(
    (row) => {
      const variant = "RecordingSpirometry";
      const docid = row?.id;
      if (!docid) return;

      navigate(
        `/result?variant=${encodeURIComponent(variant)}&docid=${encodeURIComponent(docid)}`,
      );
    },
    [navigate],
  );

  // Daily averages + min/max per selected metric (same logic as your pulse daily aggregation)
  const trend = useMemo(() => {
    if (!Array.isArray(rows) || !rows.length) {
      return { labels: [], xValues: [], values: [], minMax: [], counts: [] };
    }

    const chron = [...rows].reverse();

    const samples = [];
    for (const r of chron) {
      const ms = r?.timestamp ? Date.parse(r.timestamp) : NaN;
      if (!Number.isFinite(ms)) continue;

      const raw = getValidatedMeasurementValue(
        metric === "FEV1" ? "fev1" : "pef",
        metric === "FEV1" ? r?.fev1 : r?.pef,
      );

      // IMPORTANT: don't coerce null/""/"--" into 0
      if (typeof raw !== "number" || !Number.isFinite(raw)) continue;

      samples.push({ ms, v: raw });
    }

    const byDay = new Map(); // dayStartMs -> { sum,n,min,max }
    for (const s of samples) {
      const d = new Date(s.ms);
      const dayStartMs = new Date(
        d.getFullYear(),
        d.getMonth(),
        d.getDate(),
      ).getTime();

      const cur = byDay.get(dayStartMs) || { sum: 0, n: 0, min: s.v, max: s.v };
      cur.sum += s.v;
      cur.n += 1;
      cur.min = Math.min(cur.min, s.v);
      cur.max = Math.max(cur.max, s.v);
      byDay.set(dayStartMs, cur);
    }

    const days = Array.from(byDay.entries())
      .map(([dayStartMs, agg]) => ({ dayStartMs, ...agg }))
      .sort((a, b) => a.dayStartMs - b.dayStartMs);

    const xValues = days.map((d) => d.dayStartMs);
    const values = days.map((d) => d.sum / d.n);
    const minMax = days.map((d) => ({ min: d.min, max: d.max }));
    const counts = days.map((d) => d.n);
    const labels = xValues.map(labelDdMm);

    return { labels, xValues, values, minMax, counts };
  }, [rows, metric]);

  if (!uid || loading) {
    return (
      <div className="min-h-64 flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (error) return <div className="muted-text">Failed to load results.</div>;
  if (!rows.length) return <div className="muted-text">No results yet.</div>;

  const yUnit = metric === "FEV1" ? "L" : "L/min";
  const yDecimals = metric === "FEV1" ? 2 : 0;
  const lineLabel = metric === "FEV1" ? "FEV1 (Avg)" : "PEF (Avg)";

  return (
    <div className="w-full h-full min-h-0 flex flex-col gap-2">
      {/* Metric toggle (only affects chart) */}
      {/* <div className="w-full flex gap-2 pt-2">
        <button
          type="button"
          className={[
            "px-2 py-1 rounded-xl text-sm",
            metric === "FEV1"
              ? "bg-primary text-greyscale-1"
              : "bg-greyscale-1 text-primary",
          ].join(" ")}
          onClick={() => setMetric("FEV1")}
        >
          {t("results.spiro.fev1", "FEV1")}
        </button>
        <button
          type="button"
          className={[
            "px-2 py-1 rounded-xl text-sm",
            metric === "PEF"
              ? "bg-primary text-greyscale-1"
              : "bg-greyscale-1 text-primary",
          ].join(" ")}
          onClick={() => setMetric("PEF")}
        >
          {t("results.spiro.pef", "PEF")}
        </button>
      </div> */}

      {/* Trend chart */}
      {/* <MetricTrendChart
        height={190}
        labels={trend.labels}
        values={trend.values}
        xValues={trend.xValues}
        xTickFormat="dd/MM"
        lineLabel={lineLabel}
        yUnit={yUnit}
        yDecimals={yDecimals}
        showMinMaxBars={true}
        minMax={trend.minMax}
        counts={trend.counts}
        showTrendLine={true}
      /> */}

      {/* scrolling list */}
      <div
        className="w-full flex-1 min-h-0 overflow-y-auto"
        onScroll={handleScroll}
      >
        {rows.map((r, idx) => (
          <SpirometryResultRow
            key={r.id ?? idx}
            time={r.time}
            date={r.date}
            fev1={r.fev1}
            pef={r.pef}
            onClick={() => onRowClick(r)}
          />
        ))}
        {loadingMore ? <Spinner size={24} className="py-4" /> : null}
      </div>
    </div>
  );
});
